/**
 * Fill Forms' card, from each form's fill as the form screen reads it
 * (`formFill`): done only when every form holds answers. A form the case was
 * created with, or one whose fill ran and filled nothing, isn't filled. Pure
 * so the rules are testable.
 */
import type { FormFill } from "../../casePrepUscisForms/fillProgress";
import type { WorkspaceItem } from "../workspace/workspaceModel";
import type { FileStamp } from "./status";
import type { WorkflowView } from "./types";

export type FormsView = Pick<WorkflowView, "status" | "detail" | "reasons">;

/** A form item's fill; an item built without one reads from its status. */
const fillOf = (item: WorkspaceItem): FormFill => {
  if (item.fill) return item.fill;
  const state =
    item.status === "running"
      ? "filling"
      : item.status === "failed"
      ? "failed"
      : item.status === "ready" || item.status === "stale"
      ? "filled"
      : "unfilled";
  return {
    formNumber: item.artifact.title,
    state,
    answered: 0,
    total: null,
    notStarted: state === "unfilled",
    progress: null,
    error: state === "failed" ? item.detail?.trim() || "the fill failed" : null,
    errorDetail: null
  };
};

const count = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/** "I-129: 0 of 140 fields filled", "G-28: not started". */
const fieldsLine = (f: FormFill): string => {
  if (f.total !== null)
    return `${f.formNumber}: ${f.answered} of ${f.total} fields filled`;
  if (f.answered)
    return `${f.formNumber}: ${count(f.answered, "field")} filled`;
  return `${f.formNumber}: ${
    f.notStarted ? "not started" : "no fields filled"
  }`;
};

/** "I-907 failed: why", the server's wording on a second line. */
const failedLine = (f: FormFill) =>
  `${f.formNumber} failed: ${f.error ?? "the fill failed"}${
    f.errorDetail ? `\n${f.errorDetail}` : ""
  }`;

const fillingLine = (f: FormFill) =>
  f.progress ?? `Filling ${f.formNumber} in the background`;

/** "I-907 failed" for one form, "2 forms failed" for more. */
const named = (fills: FormFill[], one: (f: FormFill) => string, many: string) =>
  fills.length === 1 ? one(fills[0]) : `${count(fills.length, "form")} ${many}`;

export const formsView = (items: readonly WorkspaceItem[]): FormsView => {
  const fills = items.map(fillOf);
  if (!fills.length) return { status: "ready", detail: "No forms yet" };
  const of = (s: FormFill["state"]) => fills.filter((f) => f.state === s);
  const filling = of("filling");
  const failed = of("failed");
  const unfilled = of("unfilled");
  const filled = of("filled");
  const pending = [...failed.map(failedLine), ...unfilled.map(fieldsLine)];
  if (filling.length)
    return {
      status: "running",
      detail:
        filling.length === 1
          ? `Filling ${filling[0].formNumber} in the background`
          : `Filling ${count(filling.length, "form")} in the background`,
      reasons: [...filling.map(fillingLine), ...pending]
    };
  if (failed.length)
    return {
      status: "failed",
      detail: named(failed, (f) => `${f.formNumber} failed`, "failed"),
      reasons: pending
    };
  if (unfilled.length)
    return {
      status: "ready",
      detail: filled.length
        ? `${filled.length} of ${count(fills.length, "form")} filled`
        : named(unfilled, (f) => `${f.formNumber} not filled`, "not filled"),
      reasons: pending
    };
  return {
    status: "done",
    detail: named(filled, (f) => `${f.formNumber} filled`, "filled"),
    reasons: [
      ...filled
        .filter((f) => f.error)
        .map(
          (f) => `${f.formNumber}: the last fill changed nothing (${f.error})`
        ),
      ...filled.map(fieldsLine)
    ]
  };
};

/**
 * Every form is filled and no file arrived after the oldest fill, so a
 * chained run has nothing for Fill Forms to do.
 */
export const formsCurrent = (
  items: readonly WorkspaceItem[],
  stamps: readonly FileStamp[]
): boolean => {
  if (formsView(items).status !== "done") return false;
  const oldest = items
    .map((i) => i.updatedAt ?? "")
    .reduce((min, at) => (!min || (at && at < min) ? at : min), "");
  if (!oldest) return true;
  const since = Date.parse(oldest);
  return !stamps.some((f) => !!f.addedAt && Date.parse(f.addedAt) > since);
};

/** "G-28", "G-28 and I-129", "G-28, I-129 and I-907". */
const andList = (names: string[]) =>
  names.length < 2
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

export type PacketForms = {
  /** "Built without G-28 and I-129: …"; null when every form is in it. */
  leftOut: string | null;
  leftOutCount: number;
  /** "I-129 was filled after this packet was built", per form. */
  filledAfter: string[];
};

/**
 * What the built packet did with the case's forms. A build prints only forms
 * with a filled PDF and leaves the rest out (it never prints one blank), so
 * a form not filled at build time isn't in it.
 */
export const packetForms = (
  forms: readonly WorkspaceItem[],
  packet: WorkspaceItem
): PacketForms => {
  const printed = new Set(packet.packetForms ?? []);
  const builtAt = packet.updatedAt ? Date.parse(packet.updatedAt) : NaN;
  const outside = forms.filter((f) => !printed.has(f.artifact.id));
  const unfilled = outside.filter((f) => fillOf(f).state !== "filled");
  const filledAfter = outside.filter(
    (f) =>
      fillOf(f).state === "filled" &&
      !!f.updatedAt &&
      Date.parse(f.updatedAt) > builtAt
  );
  const names = unfilled.map((f) => fillOf(f).formNumber);
  const one = names.length === 1;
  return {
    leftOut: names.length
      ? `Built without ${andList(names)}: ${
          one ? "it isn't" : "they aren't"
        } filled, so the packet leaves ${one ? "it" : "them"} out`
      : null,
    leftOutCount: names.length,
    filledAfter: filledAfter.map(
      (f) => `${fillOf(f).formNumber} was filled after this packet was built`
    )
  };
};
